import plusIcon from "assets/images/goals/plusIcon.svg";
import { BlockButtonGroup } from "common/styleguide/BlockButtonGroup";
import { ConfirmationDialog } from "common/styleguide/ConfirmationDialog";
import { ImageUpload, reduceBlobURL } from "common/styleguide/ImageUpload";
import { InteriorLabelInput } from "common/styleguide/InteriorLabelInput";
import { RadioGroup } from "common/styleguide/RadioGroup";
import { Milestone } from "data/records";
import { milestoneActions, milestoneSelectors } from "data/resources/milestones";
import { messages as milestoneMessages } from "features/challenges/admin/MilestoneConfig";
import { getFormattedValue, setFormattedValue } from "features/challenges/challengeUtils";
import { Map } from "immutable";
import { commonMetrics, commonNouns, commonVerbs, useFormatMessage } from "lib/localization";
import { errorMessage } from "lib/styleguide/base.scss";
import { c } from "lib/textUtils";
import { unitMessages } from "lib/units/unitTypes";
import { getPreferences } from "lib/utils";
import React, { useState } from "react";
import { defineMessages } from "react-intl";
import { connect } from "react-redux";
import distanceBadge from "./badges/distance.png";
import elevationBadge from "./badges/elevation.png";
import timeBadge from "./badges/time.png";
import styles from "./CreateEditMilestoneModal.scss";

export const CREATE_EDIT_MILESTONE_MODAL = "CREATE_EDIT_MILESTONE_MODAL";

const messages = defineMessages({
  uploadCustom: {
    id: "CreateEditMilestoneModal.uploadCustom",
    defaultMessage: "Upload custom badge",
  },
  choosePreset: {
    id: "CreateEditMilestoneModal.choosePreset",
    defaultMessage: "Choose preset badge",
  },
  deletePrevious: {
    id: "CreateEditMilestoneModal.deletePrevious",
    defaultMessage: "Delete your previous Milestones before making this change.",
  },
  targetValueError: {
    id: "CreateEditMilestoneModal.targetValueError",
    defaultMessage: "Target value must be different from other milestones",
  },
});

const typeOptions = (goalType, fm) => {
  let valueMsg;
  switch (goalType) {
    case "distance":
      valueMsg = commonMetrics.distance;
      break;
    case "moving_time":
      valueMsg = commonMetrics.time;
      break;
    case "elevation_gain":
      valueMsg = commonMetrics.elevation;
      break;
  }
  return Map({
    percent: fm(milestoneMessages.percentage),
    value: fm(valueMsg),
  });
};

const logoOptions = (fm) =>
  Map({
    custom: fm(messages.uploadCustom),
    preset: fm(messages.choosePreset),
  });

const presetOptions = Map({
  distance: <img height="150" width="150" src={distanceBadge} />,
  elevation: <img height="150" width="150" src={elevationBadge} />,
  time: <img height="150" width="150" src={timeBadge} />,
});

const REQUIRED_ATTRS = ["title", "targetType", "targetValue"];
const isValid = (m) => {
  return REQUIRED_ATTRS.filter((attr) => !m.get(attr)).length === 0;
};

export const _CreateEditMilestoneModal = ({
  goal,
  goalType,
  goalParams,
  targetType,
  milestone,
  allowTargetChange,
  createMilestone,
  updateMilestone,
  onRequestClose,
}) => {
  const fm = useFormatMessage();
  const { metric_units: metricUnits } = getPreferences();
  const [showTargetError, setShowTargetError] = useState(false);
  const [pendingEdits, setPendingEdits] = useState(targetType ? Map({ targetType }) : Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState({
    title: false,
    targetType: false,
    targetValue: false,
  });
  const [logoType, setLogoType] = useState("preset");

  const setPendingEdit = (...args) => {
    if (typeof args[0] === "object") {
      const nextEdits = pendingEdits.withMutations((p) => {
        Object.keys(args[0]).forEach((k) => {
          if (pendingEdits.get(k) === args[0][k]) {
            p.delete(k);
          } else {
            p.set(k, args[0][k]);
          }
        });
        return p;
      });
      setPendingEdits(nextEdits);
    } else {
      const [k, v] = args;
      setPendingEdits(pendingEdits.set(k, v));
    }
  };
  const onBadgeDrop = (files) => {
    if (files && files[0]) {
      setPendingEdit("badge", files[0]);
    }
  };
  const onTargetChange = (e) => {
    if (!allowTargetChange) {
      setShowTargetError(true);
      return;
    }

    if (!goalParams.get("has_target")) {
      return;
    }

    setPendingEdits(
      pendingEdits.withMutations((p) => {
        p.set("targetType", e);
        if (e !== milestone?.get("targetType")) {
          p.set("targetValue", "");
        }
      })
    );
  };

  let label;
  const values = milestone ? milestone.merge(pendingEdits) : pendingEdits;
  if (values.get("targetType") === "percent" || !values.get("targetType")) {
    label = "%";
  } else {
    if (goalType === "distance") {
      label = fm(metricUnits ? unitMessages.kmShort : unitMessages.miShort);
    } else if (goalType === "elevation_gain") {
      label = fm(metricUnits ? unitMessages.mShort : unitMessages.ftShort);
    } else {
      label = fm(unitMessages.hrsShort);
    }
  }

  const onTargetValueChange = (e) => {
    const parsed = parseInt(e, 10);
    let targetValueMax;
    if (values.get("targetType") === "percent") {
      targetValueMax = 99;
    } else {
      targetValueMax = goalParams.get("has_target")
        ? Math.round(getFormattedValue(goalParams.get("target_amount"), goal.goalType, metricUnits)) - 1
        : null;
    }

    if (targetValueMax && parsed > targetValueMax) {
      setPendingEdit(
        "targetValue",
        values.get("targetType") === "percent"
          ? targetValueMax
          : Math.floor(setFormattedValue(targetValueMax, goal.goalType, metricUnits))
      );
    } else if (parsed < 0) {
      setPendingEdit("targetValue", 0);
    } else {
      setPendingEdit(
        "targetValue",
        values.get("targetType") === "percent" ? parsed : setFormattedValue(parsed, goal.goalType, metricUnits)
      );
    }
  };

  const onSubmit = () => {
    if (loading || pendingEdits.size === 0) return;
    if (!isValid(values)) {
      setError(
        REQUIRED_ATTRS.reduce((acc, curr) => {
          if (!values.get(curr)) {
            acc[curr] = true;
          }
          return acc;
        }, {})
      );
      return;
    }
    setLoading(true);

    let promise;
    if (milestone) {
      const params = Milestone.serialize(pendingEdits, [
        "title",
        "overview",
        "targetType",
        "targetValue",
        "badge",
        "badgePreset",
      ]);
      promise = () => updateMilestone(milestone.id, params);
    } else {
      promise = () => createMilestone(pendingEdits.set("goalId", goal.id));
    }
    promise()
      .then(() => {
        setLoading(false);
        onRequestClose();
      })
      .catch((e) => {
        const { target, error_code: errorCode } = e.details[0];
        if (target === "target_value" && errorCode === "validation_failed") {
          setError({ ...error, targetValue: true });
        }
      });
  };

  return (
    <ConfirmationDialog
      title={fm(milestoneMessages.add)}
      maxWidth="600px"
      onRequestClose={onRequestClose}
      confirmText={fm(commonVerbs.save)}
      confirmPending={loading}
      confirmDisabled={pendingEdits.size === 0}
      onConfirm={onSubmit}
    >
      <form onSubmit={onSubmit}>
        <label className={error.title && errorMessage}>
          {c(fm(commonNouns.title))}
          <input value={values.title} onChange={(e) => setPendingEdit("title", e.target.value)} />
        </label>
        <label>
          {fm(commonNouns.overview)}
          <input value={values.overview} onChange={(e) => setPendingEdit("overview", e.target.value)} />
        </label>
        <label className={error.targetType && errorMessage}>
          {fm(milestoneMessages.target)}
          {goal.get("goalParams")["has_target"] && (
            <RadioGroup
              style={{ margin: "0 0" }}
              horizontal
              inline
              disabled={!allowTargetChange}
              options={typeOptions(goalType, fm)}
              selected={values.get("targetType") || "distance"}
              onChange={onTargetChange}
            />
          )}
          {showTargetError && (
            <div className={errorMessage} onClick={() => setShowTargetError(false)}>
              {fm(messages.deletePrevious)}
            </div>
          )}
        </label>
        <InteriorLabelInput
          className={styles.maxWidth}
          error={error.targetValue}
          errorMessage={fm(messages.targetValueError)}
          value={
            values.get("targetType") === "percent"
              ? values.get("targetValue")
              : Math.round(getFormattedValue(values.get("targetValue"), goalType, metricUnits))
          }
          onChange={onTargetValueChange}
          label={label}
        />
        <div className={styles.badges}>
          <label>{fm(commonNouns.logo)} (150px x 150px)</label>
          <RadioGroup
            style={{ margin: "0 0" }}
            horizontal
            inline
            options={logoOptions(fm)}
            selected={logoType}
            onChange={setLogoType}
          />
          {logoType === "custom" ? (
            <ImageUpload onDrop={onBadgeDrop} className={styles.maxWidth}>
              {values.get("badge") ? (
                <img height={80} src={reduceBlobURL(values.get("badge"), values.get("updatedAt"))} />
              ) : (
                <img className={styles.plusIcon} height="80" width="80" src={plusIcon} />
              )}
            </ImageUpload>
          ) : (
            <BlockButtonGroup
              itemClassName={styles.badgeButton}
              selectedItemClassName={styles.selectedBadgeButton}
              options={presetOptions}
              selected={values.get("badgePreset")}
              onChange={(type) => setPendingEdit({ badgePreset: type, badge: null })}
            />
          )}
        </div>
      </form>
    </ConfirmationDialog>
  );
};

const mapStateToProps = (state, ownProps) => {
  const milestones = milestoneSelectors.byGoalId(state, ownProps.goal.id);
  const allowTargetChange =
    milestones.size === 0 ||
    (milestones.size > 0 &&
      milestones
        .valueSeq()
        .map((m) => m.id)
        .includes(ownProps.milestone?.id));
  return {
    allowTargetChange,
  };
};

const mapDispatchToProps = {
  createMilestone: milestoneActions.create,
  updateMilestone: milestoneActions.update,
};

export const CreateEditMilestoneModal = connect(mapStateToProps, mapDispatchToProps)(_CreateEditMilestoneModal);
